import moment from 'moment';
import { mergeWith } from 'lodash';
import { normalize } from 'normalizr';
import { createReducer } from '@reduxjs/toolkit';

import { replaceArrays } from 'shared/lodash-utils';
import {
  MentorshipProgramConnectionSchema,
  MentorshipProgramConnectionsSchema,
  MentorshipProgramRequestSchema,
  MentorshipProgramRequestsSchema,
} from 'redux/schemas/api/mentorship-program-connections';
import {
  createMentorshipProgramConnection,
  deleteMentorshipProgramConnection,
  getMentorRequest,
  getMentorRequests,
  getMentorshipProgramConnections,
  getSessionAvailability,
  redirectToNewConnectionOnApproval,
  resetMentorRequestCarouselIndex,
  sendMentorRequest,
  setCreateConnectionModalSettings,
  setMentorRequestCarouselIndex,
  updateMentorRequest,
} from 'redux/actions/mentorship-program-connections';
import { SingleMentorRequestStatus } from 'redux/schemas/models/mentor-request';

import { getUniqueKeyForTimeSlots } from 'athena/helpers/utils';
import { initialRootState } from '.';

const WORKING_HOURS = {
  startHour: 8,
  endHour: 17,
};

const setConnectionActionHandler = (state, action) => {
  const normalized = normalize(
    action.payload,
    MentorshipProgramConnectionSchema,
  );

  mergeWith(state.models, normalized.entities, replaceArrays);
};

export default createReducer(initialRootState, builder => {
  builder
    .addCase(
      createMentorshipProgramConnection.fulfilled,
      setConnectionActionHandler
    )
    .addCase(deleteMentorshipProgramConnection.fulfilled, (state, action) => {
      const { mentorshipProgramConnectionId } = action.meta.arg;
      delete state.models.mentorshipProgramConnections[
        mentorshipProgramConnectionId
      ];
    })
    .addCase(getMentorshipProgramConnections.fulfilled, (state, action) => {
      const normalized = normalize(
        action.payload,
        MentorshipProgramConnectionsSchema
      );

      mergeWith(state.models, normalized.entities, replaceArrays);
    })
    .addCase(setCreateConnectionModalSettings, (state, action) => {
      const { showModal, type, enrollmentId } = action.payload;
      state.app.connections.createConnectionModal = {
        showModal,
        type,
        enrollmentId,
      };
    })
    .addCase(getSessionAvailability.pending, (state, action) => {
      const {
        meetingDays,
        mentorshipProgramConnectionId,
        mentorshipProgramId,
        meetingDuration,
      } = action.meta.arg;

      const normalizedSlots = meetingDays.reduce((acc, day) => {
        acc[day] = {
          slots: [],
          loading: true,
        };
        return acc;
      }, {});

      mergeWith(
        state.models.mentorshipProgramConnections,
        {
          [mentorshipProgramConnectionId]: {
            sessionAvailabilitySlots: {
              [mentorshipProgramId]: {
                [meetingDuration]: normalizedSlots,
              },
            },
          },
        },
        replaceArrays
      );
    })
    .addCase(getSessionAvailability.fulfilled, (state, action) => {
      const { meta, payload: timeSlots } = action;
      const {
        mentorshipProgramConnectionId,
        mentorshipProgramId,
        meetingDuration,
        selectedDate,
      } = meta.arg;
      const uniqueKeyBasedOnParams = getUniqueKeyForTimeSlots(meta.arg);

      const normalizedSlots = normalizeSlots({
        availableDates: timeSlots.availableDates,
        selectedDate,
        meetingDuration,
      });

      mergeWith(
        state.models.mentorshipProgramConnections,
        {
          [mentorshipProgramConnectionId]: {
            sessionAvailabilitySlots: {
              [mentorshipProgramId]: {
                [meetingDuration]: normalizedSlots,
              },
            },
          },
        },
        replaceArrays
      );
      state.app.mentoringProgram.sessions.availability.error = false;
      state.app.mentoringProgram.sessions.availability.hasBeenLoaded[
        uniqueKeyBasedOnParams
      ] = true;
    })
    .addCase(getSessionAvailability.rejected, (state, action) => {
      const uniqueKeyBasedOnParams = getUniqueKeyForTimeSlots(action.meta.arg);
      state.app.mentoringProgram.sessions.availability.hasBeenLoaded[
        uniqueKeyBasedOnParams
      ] = false;
      state.app.mentoringProgram.sessions.availability.error = true;
    })
    .addCase(sendMentorRequest.fulfilled, (state, action) => {
      const normalized = normalize(
        [action.payload],
        MentorshipProgramRequestsSchema
      );

      mergeWith(state.models, normalized.entities, replaceArrays);
    })
    .addCase(updateMentorRequest.fulfilled, (state, action) => {
      const { state: requestStatus } = action.meta.arg;
      const { id } = action.payload;
      if (requestStatus === SingleMentorRequestStatus.WITHDRAW) {
        delete state.models.mentoringProgramRequests[id];
      }
    })
    .addCase(getMentorRequest.fulfilled, (state, action) => {
      const normalized = normalize(
        [action.payload],
        MentorshipProgramRequestsSchema
      );

      mergeWith(state.models, normalized.entities, replaceArrays);
    })
    .addCase(getMentorRequests.fulfilled, (state, action) => {
      const normalized = normalize(
        action.payload.connectionRequests,
        MentorshipProgramRequestsSchema
      );

      mergeWith(state.models, normalized.entities, replaceArrays);
    })
    .addCase(setMentorRequestCarouselIndex, (state, action) => {
      state.app.mentorRequestCarouselIndex = action.payload;
    })
    .addCase(resetMentorRequestCarouselIndex, (state) => {
      state.app.mentorRequestCarouselIndex = undefined;
    })
    .addCase(redirectToNewConnectionOnApproval, (state, action) => {
      const { enrollmentId } = action.payload;
      state.app.mentoringProgram.participantDriven.redirectToNewConnectionOnApproval =
        enrollmentId;
    });
});

type NormalizeSlotsParams = {
  availableDates: any,
  selectedDate?: string,
  meetingDuration?: number,
};

const normalizeSlots = ({
  availableDates,
  selectedDate,
  meetingDuration,
}: NormalizeSlotsParams): Record<string, { slots: any[]; loading: boolean }> => {
  const now = moment();

  const normalizedSlots = availableDates.reduce((acc, slot) => {
    const slotMoment = moment(slot.startDate);
    if (slotMoment.isSameOrBefore(now)) return acc;

    const day = slotMoment.format('YYYY-MM-DD');
    acc[day] ??= { slots: [], loading: false };
    acc[day].slots.push(slot);

    return acc;
  }, {});

  if (selectedDate) {
    const selectedMoment = moment(selectedDate);
    const selectedDateStr = selectedMoment.format('YYYY-MM-DD');

    const daySlots = normalizedSlots[selectedDateStr];
    if (daySlots) {
      const exists = daySlots.slots.some(
        (slot) => moment(slot.startDate).isSame(selectedMoment),
      );
      const isInWorkingHours = (
        selectedMoment.hour() >= WORKING_HOURS.startHour
        && selectedMoment.hour() < WORKING_HOURS.endHour
      );

      if (!exists && isInWorkingHours) {
        daySlots.slots.push({
          startDate: selectedMoment.toISOString(),
          endDate: selectedMoment.clone().add(meetingDuration, 'minutes').toISOString(),
        });

        daySlots.slots.sort((a, b) => moment(a.startDate).diff(moment(b.startDate)));
      }
    }
  }

  return normalizedSlots;
};

